/**
 * The engine's own trigonometry (docs/spec/05 R2). `Math.sin` and friends may differ in their last
 * bits from one JavaScript engine to the next; these are built from +, -, *, / and `Math.sqrt`
 * only, which may not, so a replay gives the same game everywhere. `sin` and `cos` are good to
 * about 1e-7, `atan2` to about 2e-6: far finer than a pixel at any distance on this screen.
 */

export const PI = 3.141592653589793;
export const TAU = PI * 2;
export const HALF_PI = PI / 2;
/** Straight up the screen. */
export const UP = -HALF_PI;
/** Straight down. */
export const DOWN = HALF_PI;

export function sin(x: number): number {
  let a = x - TAU * Math.floor((x + PI) / TAU);
  if (a > HALF_PI) a = PI - a;
  else if (a < -HALF_PI) a = -PI - a;
  const a2 = a * a;
  return a * (1 + a2 * (-1 / 6 + a2 * (1 / 120 + a2 * (-1 / 5040 + a2 * (1 / 362880 - a2 / 39916800)))));
}

export function cos(x: number): number {
  return sin(x + HALF_PI);
}

/** The angle of (x, y), in (-π, π]; 0 for the origin. */
export function atan2(y: number, x: number): number {
  if (x === 0 && y === 0) return 0;
  const ax = Math.abs(x);
  const ay = Math.abs(y);
  const z = ax > ay ? ay / ax : ax / ay;
  const z2 = z * z;
  let a = z * (0.99997726 + z2 * (-0.33262347 + z2 * (0.19354346 + z2 * (-0.11643287 + z2 * (0.05265332 - z2 * 0.0117212)))));
  if (ay > ax) a = HALF_PI - a;
  if (x < 0) a = PI - a;
  return y < 0 ? -a : a;
}

/** `a` brought into (-π, π]. */
export function wrapAngle(a: number): number {
  const w = a - TAU * Math.floor((a + PI) / TAU);
  return w <= -PI ? w + TAU : w;
}

/** The shortest turn from `from` to `to`, signed. */
export function angleDiff(to: number, from: number): number {
  return wrapAngle(to - from);
}

/** `heading` turned toward `target` by at most `maxTurn`. */
export function turnToward(heading: number, target: number, maxTurn: number): number {
  const d = angleDiff(target, heading);
  if (d > maxTurn) return heading + maxTurn;
  if (d < -maxTurn) return heading - maxTurn;
  return heading + d;
}

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

export function distance(x1: number, y1: number, x2: number, y2: number): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  return Math.sqrt(dx * dx + dy * dy);
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Degrees to radians, for authoring paths. */
export function deg(d: number): number {
  return (d * PI) / 180;
}
